import {
  Button,
  Card,
  Icon,
  Input,
  Label,
  Select,
  Skeleton,
  Text,
  Title,
} from "@assetwatch/ui";
import { WorkRole } from "@assetwatch/work-authorization";
import { type FormEvent, type ReactNode, useState } from "react";
import {
  type GeneralSettingsInput,
  useUpdateGeneralSettingsMutation,
} from "../../mutations/useUpdateGeneralSettingsMutation";
import type { WorkFacility } from "../../queries/useWorkContextQuery";
import { useWorkScope } from "../WorkScopeProvider";

// Language, currency and date format are not configurable yet, so they are
// shown as fixed values rather than as inputs that appear editable.
const LANGUAGE = "English (US)";
const CURRENCY = "USD ($)";
const DATE_FORMAT = "MM/DD/YYYY";
const LOCALE_FIELDS = [
  { label: "Language", value: LANGUAGE },
  { label: "Currency", value: CURRENCY },
  { label: "Date format", value: DATE_FORMAT },
];
// Common US zones keep their abbreviation; every other zone shows its IANA name.
const TIMEZONE_LABELS: Record<string, string> = {
  "America/Chicago": "America/Chicago (CT)",
  "America/New_York": "America/New_York (ET)",
  "America/Denver": "America/Denver (MT)",
  "America/Los_Angeles": "America/Los_Angeles (PT)",
};
// Matches the legacy facility timezone list: the browser's IANA zones plus
// older aliases, de-duplicated and sorted. UTC is always included because it
// is the fallback when a facility has no valid timezone.
const LEGACY_TIMEZONE_ALIASES = [
  "US/Eastern",
  "Japan",
  "Canada/Central",
  "US/Indiana-Starke",
  "US/Central",
  "US/Mountain",
  "US/Pacific",
  "America/Kentucky/Louisville",
  "MST",
  "PST",
  "US/Arizona",
];
const TIMEZONE_OPTIONS = [
  ...new Set([
    ...Intl.supportedValuesOf("timeZone"),
    ...LEGACY_TIMEZONE_ALIASES,
    "UTC",
  ]),
].sort();
const SITE_NAME_MAX_LENGTH = 200;
const SAP_PLANT_CODE_PATTERN = /^[a-zA-Z0-9]{4}$/;
const SKELETON_KEYS = [
  "site-name",
  "language",
  "currency",
  "date-format",
  "timezone",
];

const formatTimezone = (timezone: string) =>
  TIMEZONE_LABELS[timezone] ?? timezone;
const formatSapPlantCode = (code: string | null) => code ?? "Not set";

// The form keeps the SAP Plant Code as text; an empty string saves as null.
type Draft = Omit<GeneralSettingsInput, "sapPlantCode"> & {
  sapPlantCode: string;
};

function draftFrom(facility: WorkFacility): Draft {
  return {
    siteName: facility.name,
    timezone: facility.timezone,
    sapPlantCode: facility.sapPlantCode ?? "",
  };
}

// Matches the POC account card: a 640px surface with a header bar and a body.
function AccountCard({
  actions,
  children,
}: {
  actions: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="max-w-160">
      <Card padding="none">
        {/* The DS Card lays children out with items-start, so the header and
            body need an explicit full width to span the card. */}
        <div className="flex w-full items-center justify-between gap-(--spacing-12) border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
          <Title size="sm">Account</Title>
          {actions}
        </div>
        <div className="flex w-full flex-col gap-(--spacing-16) p-(--spacing-20)">
          {children}
        </div>
      </Card>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-(--spacing-4)">
      <Label>{label}</Label>
      <Text>{value}</Text>
    </div>
  );
}

function AccountDetailsSkeleton() {
  return (
    <AccountCard actions={null}>
      <output
        aria-label="Loading account details"
        className="grid grid-cols-1 gap-(--spacing-16) md:grid-cols-2"
      >
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} height="var(--spacing-40)" width="100%" />
        ))}
      </output>
    </AccountCard>
  );
}

function AccountDetails({ facility }: { facility: WorkFacility }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(facility));
  const updateSettings = useUpdateGeneralSettingsMutation();

  const siteName = draft.siteName.trim();
  const sapPlantCode = draft.sapPlantCode.trim();
  const isSiteNameEmpty = siteName === "";
  const isSapPlantCodeInvalid =
    sapPlantCode !== "" && !SAP_PLANT_CODE_PATTERN.test(sapPlantCode);
  const isUnchanged =
    siteName === facility.name &&
    draft.timezone === facility.timezone &&
    (sapPlantCode || null) === facility.sapPlantCode;
  // The site settings PATCH is administrator-only, so other roles see the
  // details read-only instead of an Edit control that would fail on save.
  const canEdit = facility.role === WorkRole.ADMINISTRATOR;
  const canSave =
    !isSiteNameEmpty &&
    !isSapPlantCodeInvalid &&
    !isUnchanged &&
    !updateSettings.isPending;

  function startEditing() {
    setDraft(draftFrom(facility));
    setIsEditing(true);
  }

  function cancel() {
    setDraft(draftFrom(facility));
    setIsEditing(false);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    try {
      await updateSettings.mutateAsync({
        siteName,
        timezone: draft.timezone,
        sapPlantCode: sapPlantCode || null,
      });
      setIsEditing(false);
    } catch {
      // The mutation shows the error toast; stay in edit mode so the typed
      // values are kept for another attempt.
    }
  }

  const actions = isEditing ? (
    <div className="flex items-center gap-(--spacing-8)">
      <Button
        variant="ghost"
        size="sm"
        type="button"
        disabled={updateSettings.isPending}
        onClick={cancel}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        loading={updateSettings.isPending}
        disabled={!canSave}
      >
        <Icon name="check-mark" size="sm" />
        Save changes
      </Button>
    </div>
  ) : canEdit ? (
    <Button variant="ghost" size="sm" type="button" onClick={startEditing}>
      <Icon name="edit-pen" size="sm" />
      Edit
    </Button>
  ) : null;

  if (!isEditing) {
    return (
      <AccountCard actions={actions}>
        <ReadOnlyField label="Site name" value={facility.name} />
        <div className="grid grid-cols-1 gap-(--spacing-16) md:grid-cols-2">
          {LOCALE_FIELDS.map(({ label, value }) => (
            <ReadOnlyField key={label} label={label} value={value} />
          ))}
          <ReadOnlyField
            label="Timezone"
            value={formatTimezone(facility.timezone)}
          />
          <ReadOnlyField
            label="SAP Plant Code"
            value={formatSapPlantCode(facility.sapPlantCode)}
          />
        </div>
      </AccountCard>
    );
  }

  const timezoneOptions = TIMEZONE_OPTIONS.includes(draft.timezone)
    ? TIMEZONE_OPTIONS
    : [draft.timezone, ...TIMEZONE_OPTIONS];

  return (
    <form onSubmit={save} noValidate>
      <AccountCard actions={actions}>
        <Input
          label="Site name"
          value={draft.siteName}
          required
          maxLength={SITE_NAME_MAX_LENGTH}
          error={isSiteNameEmpty ? "Site name is required." : undefined}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              siteName: event.target.value,
            }))
          }
        />

        <div className="grid grid-cols-1 gap-(--spacing-16) md:grid-cols-2">
          {LOCALE_FIELDS.map(({ label, value }) => (
            <Select
              key={label}
              label={label}
              options={[{ value, label: value }]}
              value={value}
              readOnly
            />
          ))}
          <Select
            label="Timezone"
            options={timezoneOptions.map((value) => ({
              value,
              label: formatTimezone(value),
            }))}
            value={draft.timezone}
            onChange={(value) =>
              value && setDraft((current) => ({ ...current, timezone: value }))
            }
          />
          <Input
            label="SAP Plant Code"
            value={draft.sapPlantCode}
            maxLength={4}
            error={
              isSapPlantCodeInvalid
                ? "SAP Plant Code must be exactly 4 letters or numbers."
                : undefined
            }
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                sapPlantCode: event.target.value,
              }))
            }
          />
        </div>
      </AccountCard>
    </form>
  );
}

export function GeneralSettingsSection() {
  const { isError, isPending, selectedFacility } = useWorkScope();

  if (isPending) return <AccountDetailsSkeleton />;
  if (isError)
    return <Text tone="subtle">Account details could not be loaded.</Text>;
  if (!selectedFacility) return <Text tone="subtle">No site is selected.</Text>;

  // Keyed by facility so switching sites discards any unsaved draft.
  return (
    <AccountDetails key={selectedFacility.id} facility={selectedFacility} />
  );
}
